import produce from "immer";
import {updateEditingApp} from "../store/application/actions";
import {IApplication} from "../Interfaces/Application/IApplication";
import {ApplicationDispatch} from "../store/application/types";
import {BusinessRulesEngine} from "../Data/BusinessRulesData";
import {IQuotePayType} from "../Interfaces/Common/IQuotePayType";
import moment from "moment/moment";
import {
  getFinanceQuoteFeesThatAreDisplayed,
  getFinanceQuoteFeesThatAreNotCost,
  getSelectedFinanceQuote,
  getZipCodeForPrimary,
  handleFinanceQuoteZipCodeUpdate
} from "./quoteUtils";
import {IState} from "../Interfaces/Common/IState";

import {
  add, CarletonFeeType,
  divide,
  FinanceLoanClass, FinanceLoanClassFinanceTax, FinanceQuote, FinanceQuoteFee, FinanceQuoteTax, FinanceTaxType,
  getAmountFinanced, getTotalDeferredPayments,
  hasValue,
  multiply,
  subtract,
  toDollars, updateFinanceQuoteFee
} from "@amsanalytics/react-shared-library";

const getTotalTaxAmount = (financeQuote: FinanceQuote, financeLoanClass: FinanceLoanClass | undefined, states: IState[]): ITaxIdAndItsTaxAmount[] => {
  // The trade in value is used in the tax calc, but not the payoff / lien amount
  const totalTaxableVehicleAmount = subtract(financeQuote.sellingPrice, financeQuote.tradeInValue, 2);
  
  let totalTaxableFeeAmount = 0;

  financeQuote.financeQuoteFees.forEach(appFee => {
    // Figures out if the fees are taxable and then adds them to the total amount to be multiplied by the rate
    const matchedFinanceFee = financeLoanClass?.financeLoanClassFinanceFees.find(ff => ff.id === appFee.financeLoanClassFinanceFeeId);
    if (matchedFinanceFee && matchedFinanceFee.feeType === CarletonFeeType.None) { // 0 means "None" to Carleton and also means that it is taxable
      totalTaxableFeeAmount = add(totalTaxableFeeAmount, appFee.amount);
    }
  });

  const amountToApplyTaxRateForAnythingTaxable = add(totalTaxableFeeAmount, totalTaxableVehicleAmount, 2);
  
  // Loops through every tax individually and gets the tax amount for each
  const taxIdsAndTheirTaxAmount: ITaxIdAndItsTaxAmount[] = [];
  financeLoanClass?.financeLoanClassFinanceTaxes.forEach(loanClassTax => {
    const quoteTaxForFinanceTax = financeQuote.financeQuoteTaxes.find(quoteTax => quoteTax.financeLoanClassFinanceTaxId === loanClassTax.id);
    if (!quoteTaxForFinanceTax) {
      return;
    }
    
    switch (loanClassTax.financeTaxType) {
      case FinanceTaxType.AnythingTaxable:
        const anythingTaxableTaxAmount = getTaxAmount(quoteTaxForFinanceTax.rate, amountToApplyTaxRateForAnythingTaxable);
        taxIdsAndTheirTaxAmount.push({financeQuoteTaxId: quoteTaxForFinanceTax.id, taxAmount: anythingTaxableTaxAmount})
        break;
        
      case FinanceTaxType.OnlySellingPrice:
        const state = states.find(s => s.id === financeQuote.zipCode?.stateId);
        let onlySellingPriceTaxableBase = financeQuote.sellingPrice;

        // Illinois county tax is calculated on (Selling Price + Doc Fee - Trade Allowance)
        if (loanClassTax.description === "County Tax" && state?.abbreviation === "IL") {
          const docFee = getDocFeeAmount(financeLoanClass, financeQuote.financeQuoteFees);
          onlySellingPriceTaxableBase = add(onlySellingPriceTaxableBase, docFee, 2);
          onlySellingPriceTaxableBase = subtract(onlySellingPriceTaxableBase, financeQuote.tradeInValue, 2);
        }

        let onlySellingPriceTaxAmount = getTaxAmount(quoteTaxForFinanceTax.rate, onlySellingPriceTaxableBase);
        const countyMaxTaxableAmount = state?.countyMaxTaxableAmount;
        
        // If there is a max taxable amount for the zip code, it is applied below
        if (loanClassTax.description === "County Tax" && hasValue(countyMaxTaxableAmount)) {
          const maxTaxableAmount = getTaxAmount(quoteTaxForFinanceTax.rate, countyMaxTaxableAmount!);
          onlySellingPriceTaxAmount = Math.min(onlySellingPriceTaxAmount, maxTaxableAmount)
        }
        
        taxIdsAndTheirTaxAmount.push({financeQuoteTaxId: quoteTaxForFinanceTax.id, taxAmount: onlySellingPriceTaxAmount});
        break;
    }
  });
  
  return taxIdsAndTheirTaxAmount;
}

const getDocFeeAmount = (financeLoanClass: FinanceLoanClass | undefined, financeQuoteFees: FinanceQuoteFee[]): number => {
  if (!financeLoanClass) return 0;
  const docFeeLoanClassFee = financeLoanClass.financeLoanClassFinanceFees.find(
    loanClassFee => loanClassFee.description.trim().toUpperCase() === "DOC FEE"
  );
  if (!docFeeLoanClassFee) return 0;
  return financeQuoteFees.find(qf => qf.financeLoanClassFinanceFeeId === docFeeLoanClassFee.id)?.amount ?? 0;
}

const getTaxAmount = (rate: number, amountTaxIsAppliedTo: number): number => {
  return multiply(divide(rate, 100, 8), amountTaxIsAppliedTo, 2);
}


const getTotalFees = (
  financeQuote: FinanceQuote, 
  financeLoanClass: FinanceLoanClass | undefined
): number => {
  const displayedFees = getFinanceQuoteFeesThatAreDisplayed(financeQuote.financeQuoteFees, financeLoanClass?.financeLoanClassFinanceFees);
  const displayedFeesThatAreNotCost = getFinanceQuoteFeesThatAreNotCost(displayedFees, financeLoanClass?.financeLoanClassFinanceFees);
  return displayedFeesThatAreNotCost.reduce((feeTotal, fee) => feeTotal + (fee.amount ?? 0), 0);
}

export interface FinanceQuoteValues {
  totalDeferredPayments: number;
  totalTaxAmount: number;
  totalFees: number;
  taxIdsAndTheirTaxAmounts: ITaxIdAndItsTaxAmount[];
}

export interface ITaxIdAndItsTaxAmount {
  financeQuoteTaxId: string;
  taxAmount: number;
}

/** Extended FinanceQuoteTax with CarletonCalculatedAmount from the API (not yet in shared library type) */
export interface FinanceQuoteTaxFromApi extends FinanceQuoteTax {
  carletonCalculatedAmount?: number | null;
}

export interface FinanceQuoteTaxWithAmounts extends FinanceQuoteTax {
  dollarAmount?: string;
  description: string;
}

export const getFinanceQuoteValues = (
  financeQuote: FinanceQuote | undefined, 
  financeLoanClass: FinanceLoanClass | undefined, 
  states: IState[] | undefined
): FinanceQuoteValues | undefined => {
  if (!financeQuote) {
    return undefined;
  }
  
  const taxIdsAndTheirTaxAmounts = states
    ? getTotalTaxAmount(financeQuote, financeLoanClass, states)
    : [];
  
  // Totaling the tax amounts this way so that it doesn't mess with the 2 decimal place precision like reduce() does
  let totalTaxAmount = 0;
  taxIdsAndTheirTaxAmounts.forEach(tax => totalTaxAmount = add(totalTaxAmount, tax.taxAmount));
  
  return {
    totalDeferredPayments: getTotalDeferredPayments(financeQuote),
    totalTaxAmount: totalTaxAmount,
    taxIdsAndTheirTaxAmounts: taxIdsAndTheirTaxAmounts,
    totalFees: getTotalFees(financeQuote, financeLoanClass)
  }
}

export const isInterestRateValid = (enteredRate: number | undefined, minRate: number | undefined, maxRate: number | undefined): boolean =>
  hasValue(enteredRate) && hasValue(minRate) && hasValue(maxRate) && (enteredRate! >= minRate!) && (enteredRate! <= maxRate!);

export const interestRateErrorMessage = (min: number | undefined, max: number | undefined) => `The interest rate must be between ${min} and ${max}`;

export const isNumberOfPaymentsValid = (numberOfPayments: number, contractType: number, solveFor: number): boolean => {
  // For SolveFor === 4, it doesn't matter what the term is
  if (solveFor === 4) {
    return true;
  }
  
  // For Installment and Balloon contract types, the number of payments has to be greater than 1
  if (([1, 2].includes(contractType) && numberOfPayments <= 1)) {
    return false;
  }
  
  if (numberOfPayments < 1) {
    return false;
  }
  
  return true;
};

export const getNumberOfPaymentsErrorMessage = (numberOfPayments: number, contractType: number, solveFor: number): string => {
  if (solveFor === 4) {
    return '';
  }
  
  if (numberOfPayments < 1) {
    return 'The number of payments must be greater than 0';
  } else if ([1, 2].includes(contractType) && numberOfPayments <= 1){
    // Carleton Appendix 2 Error #15
    // Installment and Balloon contract types need to have number of payments > 1
    return 'For Contract types "Installment" or "Balloon", the number of payments must be greater than 1';
  }
  
  return '';
}

export const startOfDay = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export const getFirstPaymentDateErrorMessage = (firstPaymentDate: Date, contractDate: Date, interestStartDate: Date): string[] => {
  const errors: string[] = [];
  const firstPaymentStart = startOfDay(firstPaymentDate).getTime();

  if (startOfDay(contractDate).getTime() > firstPaymentStart) {
    errors.push('The first payment date must be on or after the contract date');
  }

  if (startOfDay(interestStartDate).getTime() > firstPaymentStart) {
    errors.push('The first payment date must be on or after the interest start date');
  }

  return errors;
}

export const getSecondFinanceLoanPaymentDate = (firstLoanPaymentDate: Date, paymentFrequency: number, quotePayTypes: IQuotePayType[]): Date => {
  firstLoanPaymentDate = new Date(firstLoanPaymentDate);
  firstLoanPaymentDate.setHours(0,0,0,0);
  
  return addDaysBasedOnPaymentFrequency(firstLoanPaymentDate, paymentFrequency, quotePayTypes, "Add");
}

export const getFinanceDeferredDownPaymentDates = (firstDeferredDownPaymentDate: Date, paymentFrequency: number, numberOfPayments: number, quotePayTypes: IQuotePayType[]): Date[] => {
  firstDeferredDownPaymentDate = new Date(firstDeferredDownPaymentDate);
  firstDeferredDownPaymentDate.setHours(0,0,0,0)
  const datesToReturn: Date[] = [firstDeferredDownPaymentDate];

  // For every iteration, it adds a specific interval
  for (let i = datesToReturn.length; i < numberOfPayments; i++) {
    const dateToCalculateOffOf = datesToReturn[datesToReturn.length - 1];
    datesToReturn.push(addDaysBasedOnPaymentFrequency(dateToCalculateOffOf, paymentFrequency, quotePayTypes, "Add"));
  }
  
  return datesToReturn;
}

export const addDaysBasedOnPaymentFrequency = (dateToCalculateFrom: Date, paymentFrequency: number, quotePayTypes: IQuotePayType[], addOrSubtract: "Add" | "Subtract"): Date => {
  const quotePayType = quotePayTypes.find(qpt => qpt.amspayType === paymentFrequency);
  dateToCalculateFrom = new Date(dateToCalculateFrom);
  dateToCalculateFrom.setHours(0,0,0,0)
  const dateToCalculateFromMomentDate = moment(dateToCalculateFrom);
  const modifier = addOrSubtract === "Add"
    ? 1 // Adds
    : -1 // Subtracts;
  
  switch (quotePayType?.name) {
    case("Single"):
      return dateToCalculateFromMomentDate.toDate();
    case("Monthly"):
      return dateToCalculateFromMomentDate.add(1 * modifier, "month").toDate();
    case("Semi-Monthly"):
      return dateToCalculateFromMomentDate.add(15 * modifier, "days").toDate();
    case("Bi-Weekly"):
      return dateToCalculateFromMomentDate.add(14 * modifier, "days").toDate();
    case("Weekly"):
      return dateToCalculateFromMomentDate.add(7 * modifier, "days").toDate();
    default:
      return dateToCalculateFromMomentDate.toDate();
  }
}

export const handleChangeOfPrimaryApplicantsPrimaryZipCode = async (currentApp: IApplication, financeLoanClass: FinanceLoanClass, dispatch: ApplicationDispatch, businessRulesEngine: BusinessRulesEngine) => {
  const zipCodeForPrimary = getZipCodeForPrimary(currentApp);
  
  if (!zipCodeForPrimary) {
    return;
  }
  
  const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);
    
  if (selectedFinanceQuote){
    // If the zip code isn't on the application OR the primary applicant's primary address's zip doesn't have the same first 5 digits
    if (!selectedFinanceQuote.zipCode?.zip || !zipCodeForPrimary.startsWith(selectedFinanceQuote.zipCode.zip)) {
      await handleFinanceQuoteZipCodeUpdate(currentApp, financeLoanClass, dispatch, businessRulesEngine);
    }
  }
}

export const handleUpdateFinanceQuoteFee = (currentApp: IApplication, updatedFee: FinanceQuoteFee, dispatch: ApplicationDispatch, businessRulesEngine: BusinessRulesEngine): void => {
  const updatedApp = produce(currentApp!, updatedCurrentApp => {
    // Applying the same values to each quote
    updatedCurrentApp.financeQuotes.forEach(financeQuote => {
      updateFinanceQuoteFee(financeQuote, updatedFee);
    });
  });
  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
}

export const getFinanceQuoteFee = (financeLoanClass: FinanceLoanClass, financeQuoteFees: FinanceQuoteFee[], feeDescription: string): FinanceQuoteFee | undefined => {
  const loanClassFeeId = financeLoanClass.financeLoanClassFinanceFees.find(loanClassFee => loanClassFee.description.trim().toUpperCase() === feeDescription.trim().toUpperCase())?.id;
  return hasValue(loanClassFeeId)
    ? financeQuoteFees.find(quoteFee => quoteFee.financeLoanClassFinanceFeeId === loanClassFeeId!)
    : undefined;
}

export const mapTaxAmountsToFinanceQuoteTaxes = (
  applicationQuoteTaxes: FinanceQuoteTax[], 
  financeLoanClassFinanceTaxes: FinanceLoanClassFinanceTax[]
  ): FinanceQuoteTaxWithAmounts[] => {
  return applicationQuoteTaxes.map(quoteTax => {

    // Only show Carleton-calculated amount (set when quote is solved).
    // Dollar amounts remain blank until the quote has been solved at least once.
    const carletonAmount = (quoteTax as FinanceQuoteTaxFromApi).carletonCalculatedAmount;
    const taxDescriptionForQuoteTax = financeLoanClassFinanceTaxes.find(flcft => flcft.id === quoteTax.financeLoanClassFinanceTaxId)?.description ?? "";

    const taxWithAmount: FinanceQuoteTaxWithAmounts = {
      ...quoteTax,
      dollarAmount: hasValue(carletonAmount) ? toDollars(carletonAmount!) : undefined,
      description: taxDescriptionForQuoteTax
    };

    return taxWithAmount;
  });
}

export const getFinanceQuoteTaxDollarAmount = (
  financeQuoteTaxes: FinanceQuoteTax[],
  financeLoanClassTaxes: FinanceLoanClassFinanceTax[], 
  financeQuoteValues: FinanceQuoteValues | undefined,
  financeLoanClassTaxName: string
): number => {
  const financeLoanClassTaxId = financeLoanClassTaxes.find(loanClassTax => loanClassTax.description.trim().toUpperCase() === financeLoanClassTaxName.trim().toUpperCase())?.id;
  const financeQuoteTaxId = financeQuoteTaxes.find(quoteTax => quoteTax.financeLoanClassFinanceTaxId === financeLoanClassTaxId)?.id;
  
  return financeQuoteValues?.taxIdsAndTheirTaxAmounts.find(taxIdWithAmount => taxIdWithAmount.financeQuoteTaxId === financeQuoteTaxId)?.taxAmount ?? 0;
}

/**
 * Pure function to calculate the doc stamp fee amount based on quote values.
 * Returns the calculated amount, or null if no doc stamp fee exists on the quote.
 * Can be used inside Immer's produce() to pre-calculate before a solve.
 */
export const calculateDocStampAmount = (
  financeQuote: FinanceQuote,
  financeLoanClass: FinanceLoanClass,
  financeQuoteValues: FinanceQuoteValues | undefined
): number | null => {
  const quoteFees = financeQuote.financeQuoteFees;
  const quoteTaxes = financeQuote.financeQuoteTaxes;
  const docStampFeeOnQuote = getFinanceQuoteFee(financeLoanClass, quoteFees, "DOC STAMP")
    ?? getFinanceQuoteFee(financeLoanClass, quoteFees, "DOC STAMPS");

  if (!docStampFeeOnQuote) {
    return null;
  }

  // This is the formula from AMS
  // BaseValue := round(QUOTE.SELLING_PRICE - QUOTE.TR_ALLOW + QUOTE.TR_LIEN - QUOTE.CASH_DOWN + QUOTE.SALES_TAX + QUOTE.DELIVERY_FEE + QUOTE.COUNTY_TAX + QUOTE.GAP_FEE + QUOTE.SERV_WARR_FEE);
  // Balance := CEIL((BaseValue)/100) *.35;
  // Balance2 := CEIL((Balance + BaseValue)/100) *.35;

  const docStampTaxRate = .35;
  const stateTaxDollarAmount = getFinanceQuoteTaxDollarAmount(quoteTaxes, financeLoanClass.financeLoanClassFinanceTaxes, financeQuoteValues, "State Tax");
  const countyTaxDollarAmount = getFinanceQuoteTaxDollarAmount(quoteTaxes, financeLoanClass.financeLoanClassFinanceTaxes, financeQuoteValues, "County Tax");
  const quoteDeliveryFeeAmount = getFinanceQuoteFee(financeLoanClass, quoteFees, "DELIVERY FEE")?.amount ?? 0;
  const quoteGapFeeAmount = getFinanceQuoteFee(financeLoanClass, quoteFees, "GAP FEE")?.amount ?? 0;
  const quoteServiceWarrantyFeeAmount = getFinanceQuoteFee(financeLoanClass, quoteFees, "SERV WARR FEE")?.amount ?? 0;

  // BaseValue := round(QUOTE.SELLING_PRICE - QUOTE.TR_ALLOW + QUOTE.TR_LIEN - QUOTE.CASH_DOWN + QUOTE.SALES_TAX + QUOTE.DELIVERY_FEE + QUOTE.COUNTY_TAX + QUOTE.GAP_FEE + QUOTE.SERV_WARR_FEE);
  const amountToApplyRate = 
    financeQuote.sellingPrice 
    - financeQuote.tradeInValue 
    + financeQuote.tradeInPayoff 
    - financeQuote.downPayment 
    + stateTaxDollarAmount 
    + quoteDeliveryFeeAmount 
    + countyTaxDollarAmount
    + quoteGapFeeAmount
    + quoteServiceWarrantyFeeAmount;

  // I have no idea why AMS applies the tax rate twice
  const balance = Math.ceil( multiply( divide(amountToApplyRate, 100), docStampTaxRate) ); // Balance := CEIL((BaseValue)/100) *.35;
  const balance2 = multiply( Math.ceil( divide((balance + amountToApplyRate),100) ), docStampTaxRate, 2); // Balance2 := CEIL((Balance + BaseValue)/100) *.35;

  return balance2;
}

export const handleUpdateDocStampQuoteFee = (
  financeLoanClass: FinanceLoanClass, 
  currentApp: IApplication,
  financeQuoteValues: FinanceQuoteValues | undefined,
  dispatch: ApplicationDispatch, 
  businessRulesEngine: BusinessRulesEngine
): void => {
  const financeQuote = getSelectedFinanceQuote(currentApp);
  if (!financeQuote) {
    return;
  }

  const newDocStampAmount = calculateDocStampAmount(financeQuote, financeLoanClass, financeQuoteValues);
  if (newDocStampAmount === null) {
    return;
  }

  const docStampFeeOnQuote = getFinanceQuoteFee(financeLoanClass, financeQuote.financeQuoteFees, "DOC STAMP")
    ?? getFinanceQuoteFee(financeLoanClass, financeQuote.financeQuoteFees, "DOC STAMPS");

  if (docStampFeeOnQuote && docStampFeeOnQuote.amount !== newDocStampAmount) {
    const updatedFee = {
      ...docStampFeeOnQuote,
      amount: newDocStampAmount
    };
    handleUpdateFinanceQuoteFee(currentApp!, updatedFee, dispatch, businessRulesEngine);
  }
}

// Calculates the theoretical amount financed before Carleton tells us
export const getFinanceQuoteAmountFinanced = (currentApp: IApplication | undefined, financeLoanClasses: FinanceLoanClass[]): number => {
  if (!currentApp) {
    return 0;
  }
  
  const selectedQuote = getSelectedFinanceQuote(currentApp);
  if (!selectedQuote) {
    return 0;
  }
  
  const selectedFinanceLoanClass = financeLoanClasses.find(flc => flc.id === selectedQuote.financeLoanClassId);
  if (!selectedFinanceLoanClass) {
    return 0;
  }

  return getAmountFinanced(selectedQuote, financeLoanClasses);
}